import toastr from "toastr";

export function money(number, currency = 'USD', locale = 'en-US') {
    const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
    });
    return formatter.format(number);
}

import moment from 'moment';
import 'moment-timezone';

/**
 * Format date in admin timezone (America/New_York)
 * @param {string} date - Date string from database
 * @param {string} returnIfEmpty - What to return if date is empty
 * @param {string} toFormat - Output format
 * @returns {string} Formatted date
 */
export function formatAdminDate(
    date,
    returnIfEmpty = '',
    toFormat = 'DD/MM/YYYY, HH:mm'
) {
    if (!date) return returnIfEmpty;

    const adminTimezone = 'America/New_York';
    const isISOString = typeof date === 'string' && (date.endsWith('Z') || date.includes('T'));

    if (isISOString) {
        return moment(date).tz(adminTimezone).format(toFormat);
    }

    return moment.tz(date, 'YYYY-MM-DD HH:mm:ss', adminTimezone).format(toFormat);
}

/**
 * Format date in user's local timezone
 * @param {string} date - Date string from database
 * @param {string} returnIfEmpty - What to return if date is empty
 * @param {string} toFormat - Output format
 * @param {string|null} userTimezone - Optional user timezone (if null, uses browser's timezone)
 * @returns {string} Formatted date with timezone indication
 */
export function formatUserDate(
    date,
    returnIfEmpty = '',
    toFormat = 'DD/MM/YYYY, HH:mm',
    userTimezone = null
) {
    if (!date) return returnIfEmpty;

    const isISOString = typeof date === 'string' && (date.endsWith('Z') || date.includes('T'));
    const momentDate = isISOString ? moment(date) : moment.utc(date, 'YYYY-MM-DD HH:mm:ss');

    if (userTimezone) {
        return momentDate.tz(userTimezone).format(toFormat);
    }

    return momentDate.local().format(toFormat);
}


export function copyText(text) {
    if (!text) return;

    const success = () => toastr?.success?.(this.$t('Copied!')) || alert(this.$t('Copied!'));
    const error = (err) => {
        toastr?.error?.(this.$t('Failed to copy')) || alert(this.$t('Failed to copy'));
    };

    if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(success).catch(error);
    } else {
        try {
            const textarea = Object.assign(document.createElement('textarea'), {
                value: text,
                readonly: true,
                style: 'position:absolute;left:-9999px',
            });
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy') ? success() : error();
            document.body.removeChild(textarea);
        } catch (err) {
            error(err);
        }
    }
}

/**
 * @deprecated Use formatAdminDate or formatUserDate instead
 */
export function formatDate(
    date,
    returnIfEmpty = '',
    toFormat = 'DD/MM/YYYY, HH:mm',
    fromFormat = 'YYYY-MM-DD HH:mm:ss',
    timezone = 'America/New_York'
) {
    console.warn('formatDate is deprecated. Use formatAdminDate or formatUserDate instead');
    return formatAdminDate(date, returnIfEmpty, toFormat);
}

export function formatWeightKgAndLb(lb, kg) {
    const kgValue = parseFloat(kg || 0).toFixed(2);
    const lbValue = parseFloat(lb || 0).toFixed(2);
    return `${kgValue} ${this.$t('kg')} (${lbValue} lb)`;
}

export async function showPackagesPopupByRef(order, type, title) {
    await this.$refs[type].show({
        order: order,
        title: title+' #'+order.number,
    });
}

export function doPost (url, hiddenPostParams = {})  {
    let form = document.createElement("form");
    form.action = url;
    form.method = "post";
    form.style.display = "none";

    hiddenPostParams["_token"] = document.querySelector('meta[name="csrf-token"]').content;

    for (let i in hiddenPostParams) {
        if (hiddenPostParams[i] instanceof Array) {
            for (let j in hiddenPostParams[i]) {
                let input = document.createElement("input");
                input.type = "hidden";
                input.name = i + "[]";
                input.value = hiddenPostParams[i][j];

                form.appendChild(input);
            }
        } else {
            let input = document.createElement("input");
            input.type = "hidden";
            input.name = i;
            input.value = hiddenPostParams[i];

            form.appendChild(input);
        }
    }
    document.body.appendChild(form);
    form.submit();
}

export function objectToQueryString(obj) {
    let string = Object.keys(obj)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
        .join('&');
    if (string) {
        string = '?' + string;
    }
    return string;
}

export function formatFileSize(bytes, si= true, dp= 1) {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
        return bytes + ' B';
    }

    const units = si
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    const r = 10**dp;

    do {
        bytes /= thresh;
        ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

    return bytes.toFixed(dp) + ' ' + units[u];
}

/**
 *
 * @param number
 * @param n length of decimal
 * @param x length of whole part
 * @param s sections delimiter
 * @param c decimal delimiter
 * @returns {string}
 */
export function formatNumber(number, n, x, s, c) {
    var re = '\\d(?=(\\d{' + (x || 3) + '})+' + (n > 0 ? '\\D' : '$') + ')',
        num = number.toFixed(Math.max(0, ~~n));

    return (c ? num.replace('.', c) : num).replace(new RegExp(re, 'g'), '$&' + (s || ','));
}

export function convertWeight(weight, from, to) {
    switch (from) {
        case 'kg':
           switch (to) {
               case 'lb':
                   return weight * 2.20462;
               case 'g':
                   return weight * 1000;
               case 'oz':
                   return weight * 35.274;

           }
           break;
        case 'lb':
            switch (to) {
                case 'kg':
                    return weight * 0.453592;
                case 'g':
                    return weight * 453.592;
                case 'oz':
                    return weight * 16;
            }
            break;
        case 'g':
            switch (to) {
                case 'kg':
                    return weight * 0.001;
                case 'lb':
                    return weight * 0.00220462;
                case 'oz':
                    return weight * 0.035274;
            }
            break;
        case 'oz':
            switch (to) {
                case 'kg':
                    return weight * 0.0283495;
                case 'lb':
                    return weight * 0.0625;
                case 'g':
                    return weight * 28.3495;
            }
            break;
    }
}
